import React, { useEffect, useState } from 'react';
import {
    X, Loader2, Cctv, Network, Server, Monitor, Wifi, HardDrive, Printer, Shield, Router, Box, EthernetPort,
    Hash, Cpu, ShieldCheck, Activity, Globe, Clock, CheckCircle2, XCircle,
    Fingerprint, Info, Database, Binary, Key,
    Settings, Terminal, Zap, Unplug, Share2, Layers, MapPin, Maximize2, Minimize2
} from 'lucide-react';
import baseapi from '../../services/baseapi';
import { useParams } from 'react-router-dom';
import { FirewallSidebarContent } from '../AssetTopology/AssetSidebar/AssetDetailSidebar';

// ─── Types ──────────────────────────────────────────────────────────────────────

interface DigitalTwinSidebarProps {
    open: boolean;
    assetId: number;
    assetName: string;
    assetType: string;
    assetIp: string;
    onClose: () => void;
}

export type LucideIcon = React.FC<any>;

export const getAssetIcon = (type: string): LucideIcon => {
    const icons: Record<string, LucideIcon> = {
        camera: Cctv, server: Server, servers: Server, ilo: Server, workstation: Monitor, access_point: Wifi,
        nvr: HardDrive, printer: Printer, firewall: Shield, router: Router,
        switch: Router, storage: HardDrive, other: Box, local_asset: Server, isp: Wifi,
        hub: EthernetPort, pdu: Zap
    };
    return icons[type] || Box;
};

// ─── Shared Components ─────────────────────────────────────────────────────────

const DetailCard: React.FC<{ icon: LucideIcon; label: string; value: any; status?: boolean }> = ({ icon: Icon, label, value, status }) => (
    <div className="bg-gray-800/60 p-2 rounded-lg border border-gray-700/50 hover:border-gray-600 transition-all group">
        <div className="flex items-center gap-2">
            <div className="p-1 bg-gray-900/80 rounded-md group-hover:bg-orange-600/80 transition-colors shrink-0">
                <Icon className="w-3 h-3 text-orange-500 group-hover:text-white" />
            </div>
            <div className="flex-1 min-w-0">
                <p className="text-[9px] font-bold text-gray-500 uppercase tracking-tight leading-none mb-0.5">{label}</p>
                {status !== undefined ? (
                    <div className="flex items-center gap-1">
                        {status ? <CheckCircle2 className="w-2.5 h-2.5 text-green-500" /> : <XCircle className="w-2.5 h-2.5 text-red-500" />}
                        <span className={`text-[10px] font-bold leading-none ${status ? 'text-green-500' : 'text-red-500'}`}>
                            {value}
                        </span>
                    </div>
                ) : (
                    <p className="text-[10px] font-bold text-gray-200 truncate leading-none" title={value?.toString()}>
                        {value || "-"}
                    </p>
                )}
            </div>
        </div>
    </div>
);

const MiniTable: React.FC<{
    icon: LucideIcon;
    title: string;
    iconColor: string;
    columns: { key: string; label: string }[];
    data: any[];
    loading: boolean;
    itemsPerPage?: number;
    renderCell?: (key: string, item: any) => React.ReactNode;
}> = ({ icon: Icon, title, iconColor, columns, data, loading, itemsPerPage = 10, renderCell }) => {
    const [currentPage, setCurrentPage] = React.useState(1);
    const start = (currentPage - 1) * itemsPerPage;
    const pagedData = data.slice(start, start + itemsPerPage);

    React.useEffect(() => { setCurrentPage(1); }, [data]);

    const totalPages = Math.ceil(data.length / itemsPerPage);

    return (
        <div className="overflow-hidden">
            <div className="px-2 py-1 flex items-center gap-1.5">
                <Icon className={`w-3 h-3 ${iconColor}`} />
                <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{title}</h3>
                {data.length > 0 && (
                    <span className="ml-auto text-[9px] text-gray-600 font-mono">{data.length}</span>
                )}
            </div>
            <div className="bg-gray-800/40 border border-gray-700/40 rounded-lg overflow-hidden">
                <div className="overflow-x-auto custom-scroll">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-gray-900/50 border-b border-gray-700/50">
                                {columns.map((col) => (
                                    <th key={col.key} className="px-2 py-1 text-[9px] text-gray-500 uppercase tracking-wider font-semibold">
                                        {col.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-700/30">
                            {loading ? (
                                <tr>
                                    <td colSpan={columns.length} className="py-4 text-center">
                                        <Loader2 className="w-3.5 h-3.5 text-orange-500 animate-spin mx-auto" />
                                    </td>
                                </tr>
                            ) : data.length === 0 ? (
                                <tr>
                                    <td colSpan={columns.length} className="py-3 text-center text-gray-600 text-[9px]">
                                        No data
                                    </td>
                                </tr>
                            ) : (
                                pagedData.map((item, i) => (
                                    <tr key={item.id || start + i} className="hover:bg-gray-700/20 transition-colors">
                                        {columns.map((col) => (
                                            <td key={col.key} className="px-2 py-1 text-[10px] text-gray-300">
                                                {renderCell ? renderCell(col.key, item) : (item[col.key] ?? '-')}
                                            </td>
                                        ))}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                {totalPages > 1 && (
                    <div className="bg-gray-900/40 px-2 py-1 border-t border-gray-700/30 flex justify-between items-center">
                        <span className="text-[9px] text-gray-500 font-medium">
                            {start + 1}–{Math.min(start + itemsPerPage, data.length)} of {data.length}
                        </span>
                        <div className="flex items-center gap-1">
                            <button
                                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                                disabled={currentPage === 1}
                                className="p-0.5 text-gray-400 hover:text-white disabled:opacity-30 transition-colors"
                            >
                                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                            </button>
                            <div className="bg-orange-600/10 border border-orange-500/20 text-orange-500 px-1.5 py-0.5 rounded text-[9px] font-bold min-w-[18px] text-center">
                                {currentPage}
                            </div>
                            <button
                                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                                disabled={currentPage === totalPages}
                                className="p-0.5 text-gray-400 hover:text-white disabled:opacity-30 transition-colors"
                            >
                                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const ApiMiniTable: React.FC<{
    icon: LucideIcon;
    title: string;
    iconColor: string;
    columns: { key: string; label: string }[];
    endpoint: string;
    query: Record<string, any>;
    itemsPerPage?: number;
    renderCell?: (key: string, item: any) => React.ReactNode;
}> = ({ icon: Icon, title, iconColor, columns, endpoint, query, itemsPerPage = 10, renderCell }) => {
    const [data, setData] = useState<any[]>([]);
    const [totalItems, setTotalItems] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(true);

    const fetchData = React.useCallback(async (page: number) => {
        setLoading(true);
        try {
            const skip = (page - 1) * itemsPerPage;
            const res = await baseapi.get(endpoint, {
                params: { query: JSON.stringify(query), skip, limit: itemsPerPage }
            });
            if (res.data.status) {
                setData(res.data.data || []);
                setTotalItems(res.data.total || res.data.data?.length || 0);
            }
        } catch { /* silent */ }
        finally { setLoading(false); }
    }, [endpoint, JSON.stringify(query), itemsPerPage]);

    useEffect(() => {
        setCurrentPage(1);
        fetchData(1);
    }, [fetchData]);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        fetchData(page);
    };

    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const start = (currentPage - 1) * itemsPerPage;

    return (
        <div className="overflow-hidden">
            <div className="px-2 py-1 flex items-center gap-1.5">
                <Icon className={`w-3 h-3 ${iconColor}`} />
                <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{title}</h3>
                {totalItems > 0 && (
                    <span className="ml-auto text-[9px] text-gray-600 font-mono">{totalItems}</span>
                )}
            </div>
            <div className="bg-gray-800/40 border border-gray-700/40 rounded-lg overflow-hidden">
                <div className="overflow-x-auto custom-scroll">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-gray-900/50 border-b border-gray-700/50">
                                {columns.map((col) => (
                                    <th key={col.key} className="px-2 py-1 text-[9px] text-gray-500 uppercase tracking-wider font-semibold">
                                        {col.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-700/30">
                            {loading ? (
                                <tr>
                                    <td colSpan={columns.length} className="py-4 text-center">
                                        <Loader2 className="w-3.5 h-3.5 text-orange-500 animate-spin mx-auto" />
                                    </td>
                                </tr>
                            ) : data.length === 0 ? (
                                <tr>
                                    <td colSpan={columns.length} className="py-3 text-center text-gray-600 text-[9px]">
                                        No data
                                    </td>
                                </tr>
                            ) : (
                                data.map((item, i) => (
                                    <tr key={item.id || i} className="hover:bg-gray-700/20 transition-colors">
                                        {columns.map((col) => (
                                            <td key={col.key} className="px-2 py-1 text-[10px] text-gray-300">
                                                {renderCell ? renderCell(col.key, item) : (item[col.key] ?? '-')}
                                            </td>
                                        ))}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                {totalPages > 1 && (
                    <div className="bg-gray-900/40 px-2 py-1 border-t border-gray-700/30 flex justify-between items-center">
                        <span className="text-[9px] text-gray-500 font-medium">
                            {start + 1}–{Math.min(start + itemsPerPage, totalItems)} of {totalItems}
                        </span>
                        <div className="flex items-center gap-1">
                            <button
                                onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                                disabled={currentPage === 1}
                                className="p-0.5 text-gray-400 hover:text-white disabled:opacity-30 transition-colors"
                            >
                                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                            </button>
                            <div className="bg-orange-600/10 border border-orange-500/20 text-orange-500 px-1.5 py-0.5 rounded text-[9px] font-bold min-w-[18px] text-center">
                                {currentPage}
                            </div>
                            <button
                                onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                                disabled={currentPage === totalPages}
                                className="p-0.5 text-gray-400 hover:text-white disabled:opacity-30 transition-colors"
                            >
                                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// ─── Helpers ────────────────────────────────────────────────────────────────────

const formatDate = (dateString: string) => {
    if (!dateString || dateString === '-') return '-';
    try {
        const normalized = /[zZ]|[+-]\d{2}:\d{2}$/.test(dateString) ? dateString : `${dateString}Z`;
        const date = new Date(normalized);
        if (isNaN(date.getTime())) return dateString;
        return date.toLocaleString('en-IN', {
            year: 'numeric', month: 'short', day: 'numeric',
            hour: '2-digit', minute: '2-digit', hour12: true,
        });
    } catch { return dateString; }
};


// ─── Video Player ──────────────────────────────────────────────────────────────

interface CameraType {
    id: string;
    name: string;
    status: string;
    url: string;
    client_id?: string;
    agent_id?: string;
}

const VideoPlayer = ({ camera, onStateChange }: { camera: CameraType; onStateChange?: (state: 'connecting' | 'live' | 'offline') => void }) => {
    const videoRef = React.useRef<HTMLVideoElement>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        let pc: RTCPeerConnection | null = null;
        let hls: any = null;
        let retryTimer: any = null;
        let retries = 0;
        const MAX_RETRIES = 6;

        let streamId = '';
        let readToken = '';
        let hlsUrl = '';
        let isUsingHlsFallback = false;
        let isMounted = true;

        const startHls = (url: string, token: string) => {
            if (!isMounted) return;
            const Hls = (window as any).Hls;
            if (!Hls) {
                setError("HLS library not loaded");
                onStateChange?.('offline');
                return;
            }

            if (hls) hls.destroy();

            hls = new Hls({
                debug: false,
                manifestLoadingTimeOut: 65000,
                xhrSetup: function (xhr: any, url: string) {
                    if (token && !url.includes('token=')) {
                        xhr.open('GET', url + (url.includes('?') ? '&' : '?') + 'token=' + token, true);
                    }
                },
            });

            hls.on(Hls.Events.ERROR, (_: any, d: any) => {
                if (!isMounted) return;
                if (d.fatal) {
                    hls.destroy();
                    hls = null;
                    if (retries < 8) {
                        retries++;
                        onStateChange?.('connecting');
                        retryTimer = setTimeout(() => {
                            if (isMounted) startHls(hlsUrl, readToken);
                        }, 3000);
                    } else {
                        setError("Camera fallback stream unavailable");
                        onStateChange?.('offline');
                    }
                }
            });

            hls.on(Hls.Events.MANIFEST_PARSED, () => {
                if (!isMounted) return;
                setError(null);
                onStateChange?.('live');
                video.play().catch(e => console.warn('autoplay: ' + e));
            });

            hls.loadSource(url);
            hls.attachMedia(video);
        };

        const refreshStream = async () => {
            if (!isMounted) return null;
            console.log("[VideoPlayer] Requesting stream access for:", camera);
            try {
                const response = await baseapi.post('/camera/stream-access', {
                    asset_id: camera.id,
                    client_id: camera.client_id,
                    agent_id: camera.agent_id,
                });
                console.log("[VideoPlayer] Stream access response:", response.data);
                if (isMounted && response.data.status) return response.data.data;
            } catch (err) {
                console.error("[VideoPlayer] Failed to refresh stream", err);
            }
            return null;
        };

        const startHlsFallback = () => {
            if (!isMounted) return;
            onStateChange?.('connecting');
            isUsingHlsFallback = true;
            if (pc) { pc.close(); pc = null; }
            if (video && video.srcObject) video.srcObject = null;
            if (hlsUrl) {
                const token = readToken || hlsUrl.split('token=')[1] || '';
                startHls(hlsUrl, token);
            } else {
                setError("Camera stream unavailable");
                onStateChange?.('offline');
            }
        };

        const handleRetry = () => {
            if (!isMounted || isUsingHlsFallback) return;
            if (retries >= MAX_RETRIES) {
                startHlsFallback();
                return;
            }
            retries++;
            onStateChange?.('connecting');
            if (retryTimer) clearTimeout(retryTimer);
            retryTimer = setTimeout(async () => {
                if (!isMounted) return;
                const freshData = await refreshStream();
                if (freshData) {
                    streamId = freshData.stream_id;
                    readToken = freshData.read_token;
                    hlsUrl = freshData.hls_url;
                }
                connectWebRTC();
            }, 4000);
        };

        const connectWebRTC = async () => {
            if (!isMounted || isUsingHlsFallback) return;
            if (pc) { pc.close(); pc = null; }
            video.srcObject = null;
            if (!streamId) {
                startHlsFallback();
                return;
            }

            try {
                pc = new RTCPeerConnection({ iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] });
                pc.addTransceiver('video', { direction: 'recvonly' });
                pc.addTransceiver('audio', { direction: 'recvonly' });

                pc.ontrack = (event) => {
                    if (!isMounted) return;
                    if (!video.srcObject) video.srcObject = new MediaStream();
                    (video.srcObject as MediaStream).addTrack(event.track);
                    setError(null);
                };

                pc.oniceconnectionstatechange = () => {
                    if (!isMounted) return;
                    const state = pc?.iceConnectionState;
                    if (state === 'connected' || state === 'completed') {
                        retries = 0;
                        setError(null);
                        onStateChange?.('live');
                    } else if (state === 'failed' || state === 'disconnected') {
                        handleRetry();
                    }
                };

                const offer = await pc.createOffer();
                if (!isMounted) return;
                await pc.setLocalDescription(offer);

                // Wait for ICE gathering
                await new Promise<void>((resolve) => {
                    if (!isMounted || pc?.iceGatheringState === 'complete') {
                        resolve();
                        return;
                    }
                    const checkGathering = () => {
                        if (!isMounted || pc?.iceGatheringState === 'complete') {
                            pc?.removeEventListener('icegatheringstatechange', checkGathering);
                            resolve();
                        }
                    };
                    pc?.addEventListener('icegatheringstatechange', checkGathering);
                    setTimeout(() => {
                        pc?.removeEventListener('icegatheringstatechange', checkGathering);
                        resolve();
                    }, 4000);
                });

                if (!isMounted) return;

                const whepUrl = `https://${window.location.hostname}/stream/${streamId}/whep?token=${readToken}`;
                const response = await fetch(whepUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/sdp' },
                    body: pc.localDescription?.sdp
                });

                if (!isMounted) return;
                if (!response.ok) throw new Error(`WHEP failed with status: ${response.status}`);

                const answerSdp = await response.text();
                if (!isMounted) return;
                await pc.setRemoteDescription(new RTCSessionDescription({ type: 'answer', sdp: answerSdp }));
            } catch (err) {
                handleRetry();
            }
        };

        const initStream = async () => {
            onStateChange?.('connecting');
            const data = await refreshStream();
            if (!isMounted) return;
            if (data) {
                streamId = data.stream_id;
                readToken = data.read_token;
                hlsUrl = data.hls_url;
                if (streamId) connectWebRTC();
                else startHlsFallback();
            } else {
                setError("Failed to get stream access");
                onStateChange?.('offline');
            }
        };

        initStream();

        return () => {
            isMounted = false;
            if (retryTimer) clearTimeout(retryTimer);
            if (pc) pc.close();
            if (hls) hls.destroy();
        };
    }, [camera.id, camera.client_id, camera.agent_id]);

    if (error) {
        return (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/80 text-white p-4">
                <p className="text-red-500 text-[10px] mb-2 font-bold uppercase tracking-tight">{error}</p>
                <button onClick={() => window.location.reload()} className="px-3 py-1 bg-gray-800 rounded-md text-[9px] font-bold hover:bg-gray-700 transition-colors uppercase tracking-widest border border-gray-700">Retry</button>
            </div>
        );
    }

    return (
        <div className="absolute inset-0 w-full h-full flex items-center justify-center bg-black overflow-hidden group">
            <video ref={videoRef} className="w-full h-full object-contain" autoPlay muted playsInline />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
        </div>
    );
};

// ─── Camera Content ─────────────────────────────────────────────────────────────



// ─── Switch Content ─────────────────────────────────────────────────────────────

export const SwitchSidebarContent: React.FC<{ assetId: number }> = ({ assetId }) => {
    const [overview, setOverview] = useState<any>(null);
    const [poe, setPoe] = useState<any[]>([]);
    const [ports, setPorts] = useState<any[]>([]);
    const [loading, setLoading] = useState({ overview: true, poe: true, ports: true });

    useEffect(() => {
        const query = JSON.stringify({ asset_id: assetId });
        baseapi.get('/switch_details', { params: { query } }).then(res => { if (res.data.status) setOverview(res.data.data?.[0] || res.data.data); }).finally(() => setLoading(prev => ({ ...prev, overview: false })));
        baseapi.get('/poe_info', { params: { query, sort_by: 'created', sort_order: 'desc', limit: 1 } }).then(res => {
            if (res.data.status && res.data.data?.length > 0) {
                try { setPoe(typeof res.data.data[0].poe_data === 'string' ? JSON.parse(res.data.data[0].poe_data) : res.data.data[0].poe_data); } catch { setPoe([]); }
            }
        }).finally(() => setLoading(prev => ({ ...prev, poe: false })));
        baseapi.get('/ports', { params: { query } }).then(res => {
            if (res.data.status && res.data.data?.length > 0) {
                try { setPorts(typeof res.data.data[0].ports === 'string' ? JSON.parse(res.data.data[0].ports) : res.data.data[0].ports); } catch { setPorts([]); }
            }
        }).finally(() => setLoading(prev => ({ ...prev, ports: false })));
    }, [assetId]);

    return (
        <div className="space-y-3">
            {loading.overview ? <div className="flex justify-center py-4"><Loader2 className="w-4 h-4 text-orange-500 animate-spin" /></div> : overview ? (
                <div className="grid grid-cols-2 gap-1.5">
                    <DetailCard icon={Network} label="Model" value={overview.model} />
                    <DetailCard icon={Hash} label="Serial" value={overview.serial_number} />
                    <DetailCard icon={Cpu} label="IOS Version" value={overview.ios_version} />
                    <DetailCard icon={Clock} label="Uptime" value={overview.uptime || "-"} />
                    <DetailCard icon={Globe} label="IP Address" value={overview.ip_address} />
                    <DetailCard icon={Layers} label="VLANs" value={overview.total_vlans} />
                    <DetailCard icon={Activity} label="Ports Up" value={`${overview.total_ports_up || 0} / ${overview.total_interfaces || 0}`} />
                    <DetailCard icon={Fingerprint} label="MAC Entries" value={overview.total_mac_entries} />
                    <DetailCard icon={MapPin} label="Location" value={overview.location} />
                </div>
            ) : <div className="text-center py-3 text-gray-500 text-[10px]">No switch data</div>}

            <MiniTable
                icon={Zap} title="PoE Details" iconColor="text-orange-500"
                columns={[{ key: 'port', label: 'Port' }, { key: 'detection_status', label: 'Detection' }, { key: 'max_power_class', label: 'Limit' }, { key: 'power_consumption_watts', label: 'Usage' }]}
                data={poe} loading={loading.poe}
                renderCell={(key, item) => (key === 'detection_status' ? <span className={`inline-flex px-1 py-0.5 rounded text-[8px] font-bold uppercase ${item[key]?.toLowerCase()?.includes('good') ? 'bg-green-500/10 text-green-500' : 'bg-gray-700/50 text-gray-500'}`}>{item[key]}</span> : item[key])}
            />

            <ApiMiniTable
                icon={Unplug} title="Interfaces" iconColor="text-blue-500"
                endpoint="/discovered_interfaces" query={{ switch_asset_id: assetId }}
                columns={[{ key: 'port_name', label: 'Port' }, { key: 'port_status', label: 'Status' }, { key: 'port_speed', label: 'Speed' }, { key: 'vlan', label: 'VLAN' }]}
                renderCell={(key, item) => (key === 'port_status' ? <span className={`inline-flex px-1 py-0.5 rounded text-[8px] font-bold uppercase ${item.port_status?.toLowerCase() === 'up' ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500'}`}>{item.port_status}</span> : item[key])}
            />

            <ApiMiniTable
                icon={Fingerprint} title="MAC Entries" iconColor="text-orange-500"
                endpoint="/mac_table" query={{ asset_id: assetId }}
                columns={[{ key: 'mac_address', label: 'MAC' }, { key: 'bridge_port', label: 'Port' }, { key: 'vlan', label: 'VLAN' }, { key: 'vendor', label: 'Vendor' }]}
                renderCell={(key, item) => (key === 'mac_address' ? <span className="font-mono text-blue-400 text-[9px]">{item[key]}</span> : item[key])}
            />

            <ApiMiniTable
                icon={Share2} title="Neighbors" iconColor="text-emerald-500"
                endpoint="/discovered_neighbors" query={{ asset_id: assetId }}
                columns={[{ key: 'protocol', label: 'Protocol' }, { key: 'local_interface_name', label: 'Local' }, { key: 'neighbor_device_id', label: 'Device' }, { key: 'neighbor_platform', label: 'Platform' }]}
            />

            <ApiMiniTable
                icon={Layers} title="VLANs" iconColor="text-purple-500"
                endpoint="/vlans" query={{ asset_id: assetId }}
                columns={[{ key: 'vlan_id', label: 'ID' }, { key: 'name', label: 'Name' }, { key: 'status', label: 'Status' }]}
            />

            <MiniTable
                icon={Activity} title="Switch Ports" iconColor="text-cyan-500"
                columns={[{ key: 'port', label: 'Port' }, { key: 'state', label: 'State' }, { key: 'service', label: 'Service' }]}
                data={ports} loading={loading.ports}
                renderCell={(key, item) => (key === 'state' ? <span className={`inline-flex px-1 py-0.5 rounded text-[8px] font-bold uppercase ${item.state?.toLowerCase() === 'open' ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500'}`}>{item.state}</span> : item[key])}
            />
        </div>
    );
};

export const CameraSidebarContent: React.FC<{ assetId: number, client_id: any }> = ({ assetId, client_id }) => {
    const [overview, setOverview] = useState<any>(null);
    const [loadingOverview, setLoadingOverview] = useState(true);
    const [streamState, setStreamState] = useState<'connecting' | 'live' | 'offline'>('connecting');
    const [isFullscreen, setIsFullscreen] = useState(false);

    useEffect(() => {
        const handleFsChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };
        document.addEventListener('fullscreenchange', handleFsChange);
        return () => document.removeEventListener('fullscreenchange', handleFsChange);
    }, []);

    const toggleFullscreen = () => {
        const container = document.getElementById('camera-video-container');
        if (!document.fullscreenElement) {
            container?.requestFullscreen().catch(err => console.error(err));
        } else {
            document.exitFullscreen().catch(err => console.error(err));
        }
    };

    useEffect(() => {
        const query = JSON.stringify({ asset_id: Number(assetId), client_id: Number(client_id) });
        console.log("[CameraSidebarContent] Fetching details for:", { assetId, client_id, query });
        baseapi.get('/asset_camera_details', { params: { query } })
            .then(res => {
                console.log("[CameraSidebarContent] Details response:", res.data);
                if (res.data.status && res.data.data?.length > 0) setOverview(res.data.data[0]);
            })
            .catch((err) => { console.error("[CameraSidebarContent] API Error:", err); })
            .finally(() => setLoadingOverview(false));
    }, [assetId, client_id]);

    const cameraObj: CameraType | null = React.useMemo(() => {
        if (!overview) return null;
        return {
            id: String(assetId),
            name: overview.name,
            status: overview.reachable ? 'online' : 'offline',
            url: '',
            client_id: String(client_id),
            agent_id: String(overview.agent_id)
        };
    }, [assetId, client_id, overview]);

    return (
        <div className="space-y-3">
            {/* Live Stream Section */}
            <div id="camera-video-container" className="relative aspect-video bg-black rounded-xl overflow-hidden border border-gray-800 shadow-lg group">
                {cameraObj ? (
                    <>
                        <VideoPlayer camera={cameraObj} onStateChange={setStreamState} />
                        <div className="absolute top-2 left-2 z-10 flex items-center gap-1.5 bg-black/60 backdrop-blur-md px-2 py-0.5 rounded-full border border-white/10">
                            <span className={`w-1.5 h-1.5 rounded-full animate-pulse ${streamState === 'live' ? 'bg-emerald-500' : streamState === 'connecting' ? 'bg-amber-500' : 'bg-red-500'}`} />
                            <span className="text-[8px] font-black uppercase text-white tracking-widest">
                                {streamState === 'live' ? 'Live' : streamState === 'connecting' ? 'Connecting' : 'Offline'}
                            </span>
                        </div>
                        <button
                            onClick={toggleFullscreen}
                            className="absolute bottom-2 right-2 z-10 p-1.5 bg-black/60 backdrop-blur-md rounded-lg border border-white/10 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white/10"
                            title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
                        >
                            {isFullscreen ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
                        </button>
                    </>
                ) : (
                    <div className="absolute inset-0 flex items-center justify-center">
                        <Loader2 className="w-5 h-5 text-orange-500 animate-spin" />
                    </div>
                )}
            </div>

            {loadingOverview ? (
                <div className="flex justify-center py-4">
                    <Loader2 className="w-4 h-4 text-orange-500 animate-spin" />
                </div>
            ) : overview ? (
                <div className="grid grid-cols-2 gap-1.5">
                    <DetailCard icon={Hash} label="Serial" value={overview.serial_number} />
                    <DetailCard icon={Cpu} label="Firmware" value={overview.firmware} />
                    <DetailCard icon={ShieldCheck} label="Auth" value={overview.auth_success ? 'Success' : 'Failed'} status={overview.auth_success} />
                    <DetailCard icon={Activity} label="Status" value={overview.reachable ? 'Online' : 'Offline'} status={overview.reachable} />
                    <DetailCard icon={Wifi} label="DHCP" value={overview.dhcp === 'True' ? 'On' : 'Off'} status={overview.dhcp === 'True'} />
                    <DetailCard icon={Globe} label="ONVIF" value={overview.onvif_available ? 'Yes' : 'No'} status={overview.onvif_available} />
                    <DetailCard icon={Server} label="Method" value={overview.auth_method} />
                    <DetailCard icon={Clock} label="Updated" value={formatDate(overview.updated)} />
                </div>
            ) : (
                <div className="text-center py-3 text-gray-500 text-[10px]">No overview data</div>
            )}

            {/* {overview?.streams && (() => {
                let parsed: any[] = [];
                try { parsed = JSON.parse(overview.streams || '[]'); } catch {  }
                return parsed.length > 0 ? (
                    <div>
                        <div className="px-2 py-1 flex items-center gap-1.5">
                            <PlayCircle className="w-3 h-3 text-orange-500" />
                            <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">RTSP Streams</h3>
                        </div>
                        <div className="space-y-1">
                            {parsed.map((s: any, i: number) => (
                                <div key={i} className="bg-gray-800/40 border border-gray-700/40 rounded-lg p-1.5">
                                    <p className="text-[8px] text-gray-500 uppercase font-bold mb-0.5">{s.stream} Stream</p>
                                    <p className="text-[9px] text-blue-400 break-all font-mono leading-tight">{s.rtsp_url}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                ) : null;
            })()} */}

            <ApiMiniTable
                icon={Monitor} title="Interfaces" iconColor="text-emerald-500"
                endpoint="/asset_camera_interfaces" query={{ asset_id: assetId, client_id }}
                columns={[{ key: 'name', label: 'Name' }, { key: 'enabled', label: 'Status' }, { key: 'ipv4_address', label: 'IP' }, { key: 'mac_address', label: 'MAC' }]}
                renderCell={(key, item) => {
                    if (key === 'enabled') return <span className={`inline-flex px-1 py-0.5 rounded text-[8px] font-bold uppercase ${item.enabled ? 'bg-green-500/10 text-green-500 border border-green-500/20' : 'bg-red-500/10 text-red-500 border border-red-500/20'}`}>{item.enabled ? 'Up' : 'Down'}</span>;
                    return <span className={key === 'ipv4_address' ? 'text-blue-400 font-mono text-[9px]' : ''}>{item[key] || '-'}</span>;
                }}
            />

            <ApiMiniTable
                icon={ShieldCheck} title="Protocols" iconColor="text-blue-500"
                endpoint="/asset_camera_protocols" query={{ asset_id: assetId, client_id }}
                columns={[{ key: 'name', label: 'Name' }, { key: 'enabled', label: 'Status' }, { key: 'ports', label: 'Ports' }]}
                renderCell={(key, item) => {
                    if (key === 'enabled') return <span className={`inline-flex px-1 py-0.5 rounded text-[8px] font-bold uppercase ${item.enabled ? 'bg-green-500/10 text-green-500 border border-green-500/20' : 'bg-red-500/10 text-red-500 border border-red-500/20'}`}>{item.enabled ? 'On' : 'Off'}</span>;
                    return item[key] || '-';
                }}
            />

            <ApiMiniTable
                icon={Info} title="Access Control" iconColor="text-purple-500"
                endpoint="/asset_camera_users" query={{ asset_id: assetId, client_id }}
                columns={[{ key: 'username', label: 'User' }, { key: 'user_level', label: 'Level' }, { key: 'auth_success', label: 'Auth' }]}
                renderCell={(key, item) => {
                    if (key === 'auth_success') return item.auth_success ? <span className="text-[9px] text-green-500 font-bold flex items-center gap-0.5"><CheckCircle2 className="w-2.5 h-2.5" />OK</span> : <span className="text-[9px] text-red-500 font-bold flex items-center gap-0.5"><XCircle className="w-2.5 h-2.5" />Fail</span>;
                    if (key === 'user_level') return <span className="px-1 py-0.5 rounded text-[8px] font-bold uppercase bg-blue-500/10 text-blue-400 border border-blue-500/20">{item.user_level || 'unknown'}</span>;
                    return item[key] || '-';
                }}
            />
        </div>
    );
};

// ─── Server Content ─────────────────────────────────────────────────────────────

export const ServerSidebarContent: React.FC<{ assetId: number, client_id: any }> = ({ assetId, client_id }) => {
    const [sysInfo, setSysInfo] = useState<any>(null);
    const [bios, setBios] = useState<any>(null);
    const [tpm, setTpm] = useState<any>(null);
    const [loading, setLoading] = useState({ sys: true, bios: true, tpm: true });

    useEffect(() => {
        const query = JSON.stringify({ asset_id: assetId, client_id });
        baseapi.get('/system_info', { params: { query } }).then(res => { if (res.data.status && res.data.data?.length > 0) setSysInfo(res.data.data[0]); }).finally(() => setLoading(prev => ({ ...prev, sys: false })));
        baseapi.get('/asset_bios', { params: { query } }).then(res => { if (res.data.status && res.data.data?.length > 0) setBios(res.data.data[0]); }).finally(() => setLoading(prev => ({ ...prev, bios: false })));
        baseapi.get('/asset_tpm', { params: { query } }).then(res => { if (res.data.status && res.data.data?.length > 0) setTpm(res.data.data[0]); }).finally(() => setLoading(prev => ({ ...prev, tpm: false })));
    }, [assetId, client_id]);

    const isWindows = sysInfo?.platform?.toLowerCase() === 'windows' || sysInfo?.os_name?.toLowerCase().includes('windows');

    return (
        <div className="space-y-4">
            <div>
                <div className="px-2 py-1 flex items-center gap-1.5 mb-1"><Terminal className="w-3 h-3 text-blue-500" /><h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">System Overview</h3></div>
                {loading.sys ? <div className="flex justify-center py-4"><Loader2 className="w-3.5 h-3.5 text-orange-500 animate-spin" /></div> : sysInfo ? (
                    <div className="grid grid-cols-2 gap-1.5">
                        <DetailCard icon={Monitor} label="Hostname" value={sysInfo.host_name} />
                        <DetailCard icon={Activity} label="OS" value={sysInfo.os_name} />
                        <DetailCard icon={Cpu} label="CPU Cores" value={sysInfo.cpu_core} />
                        <DetailCard icon={Database} label="RAM" value={`${sysInfo.physical_memory} GB`} />
                        <DetailCard icon={Fingerprint} label="MAC Address" value={sysInfo.mac_address} />
                        <DetailCard icon={Clock} label="Uptime (Days)" value={sysInfo.uptime_days} />
                        <DetailCard icon={Globe} label="IP Address" value={sysInfo.ip_address} />
                        <DetailCard icon={Binary} label="Architecture" value={sysInfo.architecture} />
                        <DetailCard icon={Settings} label="Manufacturer" value={sysInfo.manufacturer} />
                        <DetailCard icon={Hash} label="Serial" value={sysInfo.serial_number} />
                    </div>
                ) : <div className="text-center py-2 text-[9px] text-gray-600">No system info available</div>}
            </div>

            <div>
                <div className="px-2 py-1 flex items-center gap-1.5 mb-1"><Info className="w-3 h-3 text-emerald-500" /><h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">BIOS / Firmware</h3></div>
                {loading.bios ? <div className="flex justify-center py-4"><Loader2 className="w-3.5 h-3.5 text-orange-500 animate-spin" /></div> : bios ? (
                    <div className="grid grid-cols-2 gap-1.5">
                        <DetailCard icon={Binary} label="Version" value={bios.smbios_bios_version || bios.version} />
                        <DetailCard icon={Settings} label="Manufacturer" value={bios.manufacturer} />
                        <DetailCard icon={Clock} label="Release Date" value={bios.release_date} />
                        <DetailCard icon={ShieldCheck} label="Primary BIOS" value={bios.primary_bios === "True" ? "Yes" : "No"} />
                    </div>
                ) : <div className="text-center py-2 text-[9px] text-gray-600">No BIOS data available</div>}
            </div>

            {isWindows && (
                <div>
                    <div className="px-2 py-1 flex items-center gap-1.5 mb-1"><ShieldCheck className="w-3 h-3 text-purple-500" /><h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Security (TPM)</h3></div>
                    {loading.tpm ? <div className="flex justify-center py-4"><Loader2 className="w-3.5 h-3.5 text-orange-500 animate-spin" /></div> : tpm ? (
                        <div className="grid grid-cols-2 gap-1.5">
                            <DetailCard icon={CheckCircle2} label="Activated" value={tpm.activated ? "Yes" : "No"} status={tpm.activated} />
                            <DetailCard icon={CheckCircle2} label="Enabled" value={tpm.enabled ? "Yes" : "No"} status={tpm.enabled} />
                            <DetailCard icon={Key} label="Spec Version" value={tpm.spec_version} />
                            <DetailCard icon={Info} label="Product" value={tpm.product_name} />
                        </div>
                    ) : <div className="text-center py-2 text-[9px] text-gray-600">No TPM data found</div>}
                </div>
            )}
        </div>
    );
};

// ─── ILO Content ─────────────────────────────────────────────────────────────

export const IloSidebarContent: React.FC<{ assetId: number }> = ({ assetId }) => {
    const [data, setData] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const query = JSON.stringify({ asset_id: assetId });
        baseapi.get('/asset_ilo', { params: { query } })
            .then(res => {
                if (res.data.status && res.data.data?.length > 0) {
                    setData(res.data.data[0]);
                }
            })
            .finally(() => setLoading(false));
    }, [assetId]);

    if (loading) return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 text-orange-500 animate-spin" /></div>;
    if (!data) return <div className="text-center py-6 text-gray-500 text-xs">No ILO data available</div>;

    const parseJson = (str: string) => {
        try { return JSON.parse(str || '[]'); } catch { return []; }
    };

    const interfaces = parseJson(data.ilo_network_interfaces);

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-2 gap-1.5">
                <DetailCard icon={Activity} label="Health" value={data.overall_health_status} status={data.overall_health_status === 'OK'} />
                <DetailCard icon={Zap} label="Power State" value={data.power_state} status={data.power_state === 'On'} />
                <DetailCard icon={Cpu} label="ILO Gen" value={`iLO ${data.ilo_generation}`} />
                <DetailCard icon={ShieldCheck} label="Firmware" value={data.ilo_firmware} />
                <DetailCard icon={Zap} label="Power Consumed" value={`${data.power_consumed_watts} W`} />
                <DetailCard icon={Database} label="Memory Bus" value={`${data.memory_bus_util_pct}%`} />
                <DetailCard icon={Activity} label="CPU Util" value={`${data.cpu_util_pct}%`} />
                <DetailCard icon={Hash} label="Issues" value={`${data.critical_issues_count}C / ${data.warnings_count}W`} />
                <DetailCard icon={Globe} label="Target Host" value={data.target_host} />
                <DetailCard icon={Clock} label="Uptime" value={`${Math.floor(data.uptime_seconds / 3600)}h`} />
            </div>

            <MiniTable
                icon={Network} title="Management Interfaces" iconColor="text-blue-500"
                columns={[
                    { key: 'name', label: 'Name' },
                    { key: 'port_type', label: 'Type' },
                    { key: 'link_status', label: 'Status' },
                    { key: 'address', label: 'IP' }
                ]}
                data={interfaces.map((inf: any) => ({
                    ...inf,
                    address: inf.ipv4?.[0]?.address || "-"
                }))}
                loading={false}
                renderCell={(key, item) => {
                    if (key === 'link_status') return <span className={`font-bold ${item.link_status === 'LinkUp' ? 'text-green-500' : 'text-red-500'}`}>{item.link_status}</span>;
                    if (key === 'address') return <span className="text-blue-400 font-mono">{item.address}</span>;
                    return item[key];
                }}
            />
        </div>
    );
};

// ─── PDU Content ─────────────────────────────────────────────────────────────

export const PDUSidebarContent: React.FC = () => {
    const data = {
        temperature: "13.9 °C",
        humidity: "21.0 %",
        dust_density: "7.5 µg/m³",
        loads: [
            { id: 1, power: "764.8 kW", energy: "886.24 kWh", current: "3.47 A", voltage: "220.1 V" },
            { id: 2, power: "1183.6 kW", energy: "5161.93 kWh", current: "5.26 A", voltage: "225.2 V" },
            { id: 3, power: "2090.5 kW", energy: "9039.62 kWh", current: "9.44 A", voltage: "221.5 V" }
        ],
        online: true,
        display_id: "DU-PDU-054"
    };

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-2 gap-1.5">
                <DetailCard icon={Activity} label="Status" value={data.online ? 'Online' : 'Offline'} status={data.online} />
                <DetailCard icon={Hash} label="Display ID" value={data.display_id} />
                <DetailCard icon={Zap} label="Temperature" value={data.temperature} />
                <DetailCard icon={Activity} label="Humidity" value={data.humidity} />
                <DetailCard icon={Box} label="Dust Density" value={data.dust_density} />
            </div>

            <MiniTable
                icon={Zap} title="Power Loads" iconColor="text-yellow-500"
                columns={[
                    { key: 'id', label: 'Load' },
                    { key: 'voltage', label: 'Voltage' },
                    { key: 'current', label: 'Current' },
                    { key: 'power', label: 'Power' }
                ]}
                data={data.loads} loading={false}
            />
        </div>
    );
};

// ─── Main Sidebar Component ─────────────────────────────────────────────────────

const DigitalTwinSidebar: React.FC<DigitalTwinSidebarProps> = ({
    open, assetId, assetName, assetType, assetIp, onClose,
}) => {
    const { clientId } = useParams();
    const client_id = Number(clientId);
    const Icon = getAssetIcon(assetType);
    const typeLabel = assetType.charAt(0).toUpperCase() + assetType.slice(1).replace(/_/g, ' ');
    console.log(assetName);

    return (
        <div
            className={`h-full border-l border-gray-800 bg-[#0d1017] transition-all duration-300 ease-in-out flex flex-col shadow-2xl ${open ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0 pointer-events-none absolute'
                }`}
            style={{ width: open ? '380px' : '0px', minWidth: open ? '380px' : '0px' }}
        >
            <div className="h-full flex flex-col min-w-[380px]">
                <div className="px-4 py-3 border-b border-gray-800 flex-shrink-0">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2.5 min-w-0">
                            <div className={`p-1.5 rounded-lg shadow-lg ${assetType === 'camera' ? 'bg-orange-600' : assetType === 'switch' ? 'bg-blue-600' : (assetType === 'server' || assetType === 'servers') ? 'bg-indigo-600' : assetType === 'pdu' ? 'bg-yellow-600' : 'bg-slate-600'}`}><Icon className="w-4 h-4 text-white" /></div>
                            <div className="truncate">
                                <h2 className="text-xs font-black text-white truncate leading-tight tracking-tight uppercase">{assetName || assetIp || 'Selected Asset'}</h2>
                                <p className="text-[9px] font-bold text-gray-500 uppercase tracking-widest leading-tight mt-0.5">{typeLabel} • {assetIp || 'Internal'}</p>
                            </div>
                        </div>
                        <button onClick={onClose} className="p-1.5 hover:bg-gray-800 rounded-lg transition-colors text-gray-500 hover:text-white"><X className="w-4 h-4" /></button>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto custom-scroll px-4 py-4 space-y-6">
                    {(assetType === 'server' || assetType === 'servers') && <ServerSidebarContent assetId={assetId} client_id={client_id} />}
                    {assetType === 'ilo' && <IloSidebarContent assetId={assetId} />}
                    {assetType === 'camera' && <CameraSidebarContent assetId={assetId} client_id={client_id} />}
                    {assetType === 'switch' && <SwitchSidebarContent assetId={assetId} />}
                    {assetType === 'firewall' && <FirewallSidebarContent assetId={assetId} client_id={client_id} />}
                    {assetType === 'pdu' && <PDUSidebarContent />}
                    {!['server', 'servers', 'camera', 'switch', 'firewall', 'pdu', 'ilo'].includes(assetType) && (
                        <div className="py-10 text-center space-y-3"><Box className="w-10 h-10 text-gray-700 mx-auto" /><p className="text-[11px] font-bold text-gray-500 uppercase tracking-widest">Detailed metrics unavailable for this asset type </p></div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DigitalTwinSidebar;
